'use client';
import { useEffect } from 'react';
import { wsManager, WSSubscriptionTopic } from '@/lib/wsManager';
import { useGlobalStore } from '@/store/useGlobalStore';

/**
 * useWebSocket — connects the global WS manager when authenticated.
 */
export function useWebSocket() {
  const { isAuthenticated, accessToken, activeAccount } = useGlobalStore();

  useEffect(() => {
    if (isAuthenticated && accessToken && activeAccount?.account_id) {
      wsManager.connect(accessToken, activeAccount.account_id);
    } else {
      wsManager.disconnect();
    }
    return () => {
      // Do NOT disconnect on unmount — manager is global singleton.
    };
  }, [isAuthenticated, accessToken, activeAccount?.account_id]);

  // Listen for account-changed custom events
  useEffect(() => {
    const handleAccountChanged = () => {
      const accountId = typeof localStorage !== 'undefined' ? localStorage.getItem('account_id') : null;
      if (accountId) {
        wsManager.emitAccountChanged(accountId);
      }
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('account-changed', handleAccountChanged);
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('account-changed', handleAccountChanged);
      }
    };
  }, []);

  return {
    subscribe: (topic: WSSubscriptionTopic, handler: (data: unknown) => void) =>
      wsManager.subscribe(topic, handler),
    send: (eventName: string, payload: unknown) => wsManager.send(eventName, payload),
    getStatus: () => wsManager.getStatus(),
    onStatusChange: (listener: (status: string) => void) =>
      wsManager.onStatusChange(listener as Parameters<typeof wsManager.onStatusChange>[0]),
  };
}

export default useWebSocket;
